import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { DateTime } from 'luxon';

import { Typography } from 'gooey';
import { Img } from '#/components/images';
import { Page, PageSection } from '#/components/pages';
import fx from '#/fx';
import { seo } from '#/lib/head';
import { cn } from '#/utils/classnames';
import { ChangelogEntryAuthor } from './-components/changelog-entry-author';

export const Route = createFileRoute('/_main/changelog/')({
  loader: async () => {
    const entries = await fx.listChangelogEntries();
    return { entries };
  },
  head: ({ match }) => ({
    meta: seo({
      title: 'Changelog',
      description: 'Changelog for the site',
      url: match.pathname,
    }),
  }),
  component: ChangelogPage,
});

function ChangelogPage() {
  const { entries } = Route.useLoaderData();
  const nav = useNavigate();
  return (
    <Page>
      <PageSection>
        <div className='flex flex-col gap-0 mx-auto max-w-[900px]'>
          <Typography variant='h1' className='mx-auto mb-16 md:ml-36 md:mb-24'>
            Changelog
          </Typography>
          <div className='ml-4 sm:ml-8 md:ml-24'>
            {entries.map((entry) => (
              <div
                key={entry.id}
                className={cn(
                  'border-l border-solid border-gray-800 pl-4 md:pl-12 pb-12 relative pt-8 md:pt-0'
                )}
              >
                <div className='absolute -left-1 -top-2 md:-left-28'>
                  <div className='flex flex-row items-center gap-2'>
                    <div className='size-[8px] rounded-full bg-gray-500 md:hidden block' />
                    <div className='ml-2 md:ml-0 min-w-[100px]'>{formatDate(entry.date)}</div>
                    <div className='size-[8px] rounded-full bg-gray-500 hidden md:block' />
                  </div>
                </div>
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                <div
                  className='max-w-[600px] mr-auto cursor-pointer hover:animate-shine'
                  onClick={() => {
                    nav({
                      to: '/changelog/entries/$id',
                      params: { id: entry.id },
                    });
                  }}
                >
                  <div className='pb-3 px-6'>
                    <Img
                      location='static'
                      src={entry.cover.src}
                      height={entry.cover.height}
                      width={entry.cover.width}
                      alt={entry.title}
                      className='rounded-xl'
                    />
                  </div>
                  <div className='py-2 px-6'>
                    <Typography variant='h3' component='h2' className='mb-2'>
                      {entry.title}
                    </Typography>
                    <Typography variant='subtitle2' className='mb-4'>
                      {entry.summary}
                    </Typography>
                    <ChangelogEntryAuthor author={entry.author} size='medium' />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </PageSection>
    </Page>
  );
}

function formatDate(date: string) {
  return DateTime.fromISO(date).toFormat('yyyy/MM/dd');
}
